import { Checkbox, Container, FormControlLabel, FormGroup } from "@mui/material";
import React, { useRef, useEffect, useState } from "react";
import { useTheme } from '@mui/material/styles';
import Page from "src/components/Page";
import { Cesium3DTileset, Viewer } from "resium";
import { Ion, IonResource, Camera, Rectangle } from "cesium";
import * as Constants from "../map_utils/constants";
import * as LegendUtils from "../map_utils/legendUtils";
import { GeoContext } from '../GeoContext'
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import MapLegend from "src/components/MapLegend";
import * as LU from "../map_utils/legendUtils";

Ion.defaultAccessToken = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiIyMzcwOGFkZS05MTEwLTQwNzgtOTgyMS04MjkzOTA3MDc4MTQiLCJpZCI6MTMwMzg4LCJpYXQiOjE2Nzk2ODAzNDl9.mDIDtvBr9HS_ZmIYpDF1gYMn8OuTNLpSh4eUsQlgac8";


export default function Map2() {
  const theme = useTheme();

  const geoContext = React.useContext(GeoContext);

  const [showFWP, setDWP] = useState(false);
  const [showFWOP, setDWOP] = useState(false);
  const [showDamages, setDamages] = useState(false);
  const [showDWP, setFWP] = useState(false);
  const [showDWOP, setFWOP] = useState(false);
  const [roadShowWP, roadSetWP] = useState(false);
  const [roadShowWOP, roadSetWOP] = useState(false);

  // Use a local state variable to update the structure counts whenever
  // this page is first loaded. This implementation will ensure the global state
  // updates for all components (legend) when this page is loaded or re-loaded,
  // as in the case of changing locations/projects (absecon to martin).
  const [resetCounts, setResetCounts] = useState(true);

  const locationConfig = Constants.locationConfigs[geoContext.getLocation];

  const getCustomStyle = (feature_prop, breakpoints, colors, defaultColor) => {
    const strDefaultColor = `color('${defaultColor}')`;
    let conditions = [
      // First, check for NaN
      [`\${feat_prop} === null || \${feat_prop} === undefined`, strDefaultColor]
    ];

    for (let i in breakpoints) {
      const bpStr = `\${feat_prop} ${breakpoints[i]}`;
      conditions.push([bpStr, colors[i]]);
    }

    // Finally, the else case
    conditions.push(["true", strDefaultColor]);

    return new Cesium.Cesium3DTileStyle({
      defines: {
        feat_prop: `\${feature[${feature_prop}]}`,
      },
      color: {
        conditions: conditions,
      }
    });
  }

  // ////////////////////////////////////////////////////////////////////////
  // New:
  const custStyleFWP = getCustomStyle(
    Constants.wpRenamed, LegendUtils.structFloodingBps,
    LegendUtils.structFloodingColors, Constants.defaultColor);
  const custStyleFWOP = getCustomStyle(
    Constants.wopRenamed, LegendUtils.structFloodingBps,
    LegendUtils.structFloodingColors, Constants.defaultColor);

  const custStyleDamages = getCustomStyle(
    Constants.damRenamed, LegendUtils.damagesPrevBps, LegendUtils.damagesPrevColors, Constants.defaultColor);
  const custStyleDWP = getCustomStyle(
    Constants.damwpRenamed, LegendUtils.damagesBps, LegendUtils.damagesColors, Constants.defaultColor);
  const custStyleDWOP = getCustomStyle(
    Constants.damwopRenamed, LegendUtils.damagesBps, LegendUtils.damagesColors, Constants.defaultColor);

  const custStyleRFWP = getCustomStyle(
    '"water_level_above_ground_wp"',
    // '"flooding_damage_driving_param_wp"',
    LegendUtils.roadFloodingBps,
    LegendUtils.roadFloodingColors, Constants.defaultColor
  );

  const custStyleRFWOP = getCustomStyle(
    '"water_level_above_ground_wop"',
    // '"flooding_damage_driving_param_wop"',
    LegendUtils.roadFloodingBps,
    LegendUtils.roadFloodingColors, Constants.defaultColor
  );

  // Set default damages style if currently undefined:
  useEffect(() => {
    if (!geoContext.getTilesetStyle) {
      geoContext.updateTilesetStyle(custStyleDamages)
    }
    if (resetCounts) {
      setResetCounts(false);
      geoContext.updateStructureCounts(locationConfig.structureCounts[geoContext.getConsequenceType]);
    }
  });

  const changeStyleFunctions = [setFWP, setFWOP, roadSetWP, roadSetWOP, setDamages, setDWP, setDWOP];
  const changeStyle = (strucFunc, roadFunc, strucStyle, roadStyle) => {
    changeStyleFunctions.forEach(fn => {
      if (fn != strucFunc && fn != roadFunc) {
        fn(false);
      }
    });
    strucFunc(true);
    geoContext.updateTilesetStyle(strucStyle);

    if (roadFunc) roadFunc(true);
    if (roadStyle) {
      geoContext.updateRoadTilesetStyle(roadStyle);
    }
  }

  const handleChange = (event) => {
    geoContext.updateConsequenceType(event.target.value);
    geoContext.updateStructureCounts(locationConfig.structureCounts[event.target.value]);
    if (event.target.value == "fwp") {
      changeStyle(setFWP, roadSetWP, custStyleFWP, custStyleRFWP);
    } else if (event.target.value == "fwop") {
      changeStyle(setFWOP, roadSetWOP, custStyleFWOP, custStyleRFWOP);
    } else if (event.target.value == "damages") {
      changeStyle(setDamages, null, custStyleDamages, null);
    } else if (event.target.value == "dwp") {
      changeStyle(setDWP, roadSetWP, custStyleDWP, custStyleRFWP);
    } else if (event.target.value == "dwop") {
      changeStyle(setDWOP, roadSetWOP, custStyleDWOP, custStyleRFWOP);
    }
  };

  Camera.DEFAULT_VIEW_FACTOR = 0;
  Camera.DEFAULT_VIEW_RECTANGLE = Rectangle.fromDegrees(
    locationConfig.xMin, locationConfig.yMin, locationConfig.xMax, locationConfig.yMax);

  useEffect(() => {
    geoContext.updateDefaultPosition(locationConfig);
  });

  const [buildingsVisible, setBuildingsVisible] = React.useState(true);
  const handleBuildingCheckbox = (event) => {
    setBuildingsVisible(event.target.checked);
  }

  const [roadsDisabled, setRoadsDisabled] = React.useState(true);
  const [roadsVisible, setRoadsVisible] = React.useState(true);
  const handleRoadCheckbox = (event) => {
    setRoadsVisible(event.target.checked);
  }

  const renderRoads = () => {
    if (!["dwp", "damages", "dwop"].includes(geoContext.getConsequenceType)) {
      if (roadsDisabled) {
        setRoadsDisabled(false);
      }
      return (
        <Cesium3DTileset
          style={geoContext.getRoadTilesetStyle}
          url={IonResource.fromAssetId(locationConfig.assetIdRoads)}
          onTileLoad={onRoadTileLoaded}
          show={roadsVisible}
        />
      )
    }
    else {
      if (!roadsDisabled) {
        setRoadsDisabled(true);
      }
      return (<div></div>)
    }
  }

  const removeProps = (props) => {
    // Remove any undesired properties, i.e. state and any others?
    for (let i in Constants.propsToRemove) {
      let prop = Constants.propsToRemove[i];
      if (props.hasOwnProperty(prop)) {
        delete props[prop];
      }
    }
  }

  const removeRoadProps = (props) => {
    // Remove any undesired properties, i.e. state and any others?
    for (let i in Constants.roadPropsToRemove) {
      let prop = Constants.roadPropsToRemove[i];
      if (props.hasOwnProperty(prop)) {
        delete props[prop];
      }
    }
  }

  const renameProps = (props) => {
    for (const [k, v] of Object.entries(Constants.propsToRename)) {
      if (props.hasOwnProperty(k)) {
        Object.defineProperty(props, v, Object.getOwnPropertyDescriptor(props, k));
        delete props[k];
      }
    }
  }

  const reorgProps = (props, propsLen) => {
    let newProps = {};
    // First summary:
    Constants.propsOrder['summary'].forEach((key) => {
      if (Object.keys(Constants.propsToRename).includes(key)) {
        key = Constants.propsToRename[key];
      }
      newProps[key] = props[key];
    });

    // Then more detail rows:
    Constants.propsOrder['other'].forEach((key) => {
      if (Object.keys(Constants.propsToRename).includes(key)) {
        key = Constants.propsToRename[key];
      }
      newProps[key] = props[key];
    });
    return newProps;
  }

  const roundProps = (allProps) => {
    for (let i in Constants.propsToRound) {
      let props = allProps[Constants.propsToRound[i]];
      for (let j in props) {
        props[j] = Math.floor(props[j] * 100) / 100;
      }
    }
  }

  const roundRoadProps = (allProps) => {
    for (let i in Constants.roadPropsToRound) {
      let props = allProps[Constants.roadPropsToRound[i]];
      for (let j in props) {
        props[j] = Math.floor(props[j] * 100) / 100;
      }
    }
  }

  const onTileLoaded = (tile) => {
    const featLen = tile.content.batchTable.featuresLength;
    const entitiesProps = tile.content.batchTable._properties;
    removeProps(entitiesProps);
    roundProps(entitiesProps);
    renameProps(entitiesProps);
    tile.content.batchTable._properties = reorgProps(entitiesProps, featLen);
  }

  const onRoadTileLoaded = (tile) => {
    const entitiesProps = tile.content.batchTable._properties;
    removeRoadProps(entitiesProps);
    roundRoadProps(entitiesProps);
  }

  return (
    <Page>
      <Container maxWidth='xl'>
        <div>
          <h4>Toggle Map Layers: </h4>
          {/* Buildings Layer: */}
          <FormControlLabel control={
            <Checkbox
              checked={buildingsVisible}
              onChange={handleBuildingCheckbox}
            />}
            label="Buildings"
          />

          {/* Roads Layer: */}
          <FormControlLabel control={
            <Checkbox
              checked={roadsVisible}
              onChange={handleRoadCheckbox}
              disabled={roadsDisabled}
            />}
            label="Roads"
          />
        </div>

        <Viewer animation={false} timeline={false} style={{ position: "relative" }}>
          <Cesium3DTileset
            style={geoContext.getTilesetStyle}
            url={IonResource.fromAssetId(locationConfig.assetId)}
            onTileLoad={onTileLoaded}
            show={buildingsVisible}
          />
          {renderRoads()}
          <Select
            style={{
              position: "absolute", top: 10, left: 10, width: 236,
              backgroundColor: theme.palette.background.paper
            }}
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={geoContext.getConsequenceType}
            onChange={handleChange}
          >
            <MenuItem value="damages">Damages Prevented</MenuItem>
            <MenuItem value="dwop">Damages Without Project</MenuItem>
            <MenuItem value="dwp">Damages With Project</MenuItem>
            <MenuItem value="fwop">Flooding Without Project</MenuItem>
            <MenuItem value="fwp">Flooding With Project</MenuItem>
          </Select>
          <MapLegend />
        </Viewer>
      </Container>
    </Page >
  );
}
